import { useLayoutEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { ThreeDot } from 'react-loading-indicators';
import ReactMarkdown from 'react-markdown';
import {
    Box,
    CircularProgress,
    Stack,
    Table,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import OrangeBulbIconUrl from '@icons/orange-bulb-icon.svg';
import UserIconUrl from '@icons/user-icon.svg';
import Button from '@components/Button';
import ErrorMessage from '@components/ErrorMessage';
import { useAutoScrollToBottom } from '@hooks/useAutoScrollToBottom';
import { useLanguageStore } from '@store/languageStore';
import { useMessagesStore } from '@store/messagesStore';
import { useDownloadBlobFile } from '@services/queries';
import { Citation, Role } from '@services/types';

import { messageStackTexts } from '../constants';

const TomiWarning = () => {
    const theme = useTheme();
    return (
        <Typography
            variant='caption'
            fontSize={14}
            sx={{
                backgroundColor: theme.palette.customColors.violet,
                px: 1,
                py: 0.5,
                borderRadius: theme.borderRadius.tiny,
                ml: 0.5,
            }}
        >
            {messageStackTexts.tomiWarning}
        </Typography>
    );
};

interface MessageHeaderProps {
    isUser: boolean;
}

const MessageHeader = ({ isUser }: MessageHeaderProps) => {
    const icon = isUser ? UserIconUrl : OrangeBulbIconUrl;
    const label = isUser ? messageStackTexts.userLabel : messageStackTexts.tomiLabel;

    return (
        <Stack direction='row' alignItems='center' gap={1}>
            <img src={icon} alt='' aria-hidden='true' />
            <Typography fontWeight={700} fontSize={isUser ? 16 : 20}>
                {label}
            </Typography>
            {!isUser && <TomiWarning />}
        </Stack>
    );
};

interface MessageContentProps {
    content: string;
    isUser: boolean;
}

const MessageContent = ({ content, isUser }: MessageContentProps) => {
    const theme = useTheme();
    if (isUser) {
        return <Typography fontSize={16}>{content}</Typography>;
    }

    return (
        <Stack gap={2}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, rehypeSanitize]}
                components={{
                    p: ({ ...props }) => (
                        <Typography
                            component='p'
                            fontSize={16}
                            sx={{
                                '&:first-of-type': {
                                    pt: 0,
                                    mt: 0,
                                },
                                '&:last-of-type': {
                                    pb: 0,
                                    mb: 0,
                                },
                            }}
                            {...props}
                        />
                    ),
                    ol: ({ ...props }) => (
                        <Typography
                            component='ol'
                            fontSize={16}
                            sx={{
                                '&:first-of-type': { pt: 0, mt: 0 },
                                '&:last-of-type': { pb: 0, mb: 0 },
                                pl: 4,
                            }}
                            {...props}
                        />
                    ),
                    ul: ({ ...props }) => <Typography component='ul' fontSize={16} {...props} />,
                    li: ({ ...props }) => <Typography component='li' sx={{ m: 1 }} {...props} />,
                    h3: ({ ...props }) => (
                        <Typography component='h3' fontSize={16} fontWeight={700} {...props} />
                    ),
                    table: ({ ...props }) => (
                        <Table sx={{ '& td, & th': { fontSize: 16 } }} {...props} />
                    ),
                    thead: ({ ...props }) => (
                        <TableHead sx={{ backgroundColor: theme.palette.grey[200] }} {...props} />
                    ),
                    th: ({ ...props }) => (
                        <TableCell
                            {...props}
                            align='left'
                            sx={{
                                border: `${theme.borderWidth.thin} solid ${theme.palette.grey[400]}`,
                                padding: theme.spacing(1),
                            }}
                        />
                    ),
                    td: ({ ...props }) => (
                        <TableCell
                            {...props}
                            align='left'
                            sx={{
                                border: `${theme.borderWidth.thin} solid ${theme.palette.grey[400]}`,
                                padding: theme.spacing(1),
                            }}
                        />
                    ),
                    tr: ({ ...props }) => <TableRow {...props} />,
                }}
            >
                {content}
            </ReactMarkdown>
        </Stack>
    );
};

interface CitationProps {
    citation: Citation;
}

const CitationDetails = ({ citation: { title, content, absolutePath } }: CitationProps) => {
    const theme = useTheme();
    const { mutate: downloadBlobFile, isPending, error, isError } = useDownloadBlobFile(title);

    return (
        <Stack gap={2} alignItems='flex-start' component='section'>
            <Typography
                component='h4'
                fontSize={14}
                sx={{
                    backgroundColor: theme.palette.customColors.violet,
                    px: 1,
                    py: 0.5,
                    borderRadius: theme.borderRadius.tiny,
                }}
            >
                {title}
            </Typography>
            <Typography>{content}</Typography>
            {isError && <ErrorMessage error={error} />}
            <Button
                iconType='download'
                onClick={() => {
                    ReactGA.event({
                        category: 'User Interaction',
                        action: 'Citation download',
                    });

                    downloadBlobFile(absolutePath);
                }}
                disabled={isPending}
            >
                {isPending && (
                    <CircularProgress size={16} sx={{ color: theme.palette.customColors.purple }} />
                )}
                {!isPending && messageStackTexts.downloadCitationButton}
            </Button>
        </Stack>
    );
};

interface CitationsProps {
    citations: Citation[];
    scrollToBottom: () => void;
}

const Citations = ({ citations, scrollToBottom }: CitationsProps) => {
    const numberOfCitations = citations.length;
    const [showReferences, setShowReferences] = useState(false);
    const [selectedCitationId, setSelectedCitationId] = useState<number | null>(null);

    const showCitation = showReferences && selectedCitationId !== null;

    useLayoutEffect(() => {
        scrollToBottom();
    }, [scrollToBottom, showReferences, selectedCitationId]);

    return (
        <Stack gap={1} alignItems='flex-start'>
            <Button
                lighterFont
                variant={showReferences ? 'outlined' : 'contained'}
                colorVariant='purple'
                whiteText
                iconType={showReferences ? 'open-eye' : 'close-eye'}
                onClick={() => setShowReferences((prevState) => !prevState)}
            >
                {numberOfCitations} {messageStackTexts.hideShowCitationsButton}
            </Button>
            {showReferences && (
                <Stack direction='row' flexWrap='wrap' gap={1}>
                    {citations.map((citation, index) => {
                        const isOpen = selectedCitationId === index;

                        return (
                            <Button
                                lighterFont
                                variant={isOpen ? 'outlined' : 'contained'}
                                colorVariant='purple'
                                whiteText
                                iconType={isOpen ? 'open-eye' : 'close-eye'}
                                key={index}
                                onClick={() =>
                                    setSelectedCitationId((prevId) =>
                                        prevId === index ? null : index
                                    )
                                }
                            >
                                [{citation.index}] {citation.title}
                            </Button>
                        );
                    })}
                </Stack>
            )}
            {showCitation && <CitationDetails citation={citations[selectedCitationId]} />}
        </Stack>
    );
};

interface MessageProps {
    content: string;
    role: Role;
    citations?: Citation[];
    scrollToBottom: () => void;
}

const Message = ({ content, role, citations, scrollToBottom }: MessageProps) => {
    const theme = useTheme();
    const isUser = role === Role.User;
    const hasCitations = citations && citations.length > 0;

    return (
        <Stack
            gap={1}
            sx={{
                backgroundColor: isUser ? theme.palette.customColors.lightGray : '',
                alignSelf: isUser ? 'flex-end' : 'flex-start',
                padding: isUser ? theme.spacing(1) : '',
                borderRadius: isUser ? theme.borderRadius.verySmall : '',
                maxWidth: isUser ? '90%' : '100%',
            }}
        >
            <MessageHeader isUser={isUser} />
            <MessageContent content={content} isUser={isUser} />
            {hasCitations && <Citations citations={citations} scrollToBottom={scrollToBottom} />}
        </Stack>
    );
};

const LanguageRecognitionIndicator = () => {
    const theme = useTheme();
    return (
        <Box sx={{ textAlign: 'right', pr: 5, mt: 5 }}>
            <ThreeDot
                color={[theme.palette.customColors.orange, theme.palette.customColors.purple]}
                size='small'
            />
        </Box>
    );
};

const MessageStack = () => {
    const theme = useTheme();
    const messages = useMessagesStore((state) => state.messages);
    const isRecognizingLang = useLanguageStore((state) => state.isRecognizingLang);
    const { containerRef, scrollToBottom } = useAutoScrollToBottom<HTMLDivElement>(messages);

    //There are messages we need to keep on the frontend as part of the history, but we don't want to show them in the message stack
    const messagesToDisplay = messages.filter(
        (message) => message.role === Role.Assistant || message.role === Role.User
    );

    return (
        <Stack
            ref={containerRef}
            sx={{
                flexGrow: 1,
                overflowY: 'auto',
                color: theme.palette.common.black,
                paddingLeft: 1,
                paddingRight: 1,
            }}
            gap={4}
        >
            {messagesToDisplay.map(({ content, role, citations }, index) => (
                <Message
                    key={index}
                    content={content}
                    role={role}
                    citations={citations}
                    scrollToBottom={scrollToBottom}
                />
            ))}
            {isRecognizingLang && <LanguageRecognitionIndicator />}
        </Stack>
    );
};

export default MessageStack;
